import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import API_BASE_URL from "../../../config/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import axios from "axios";
import { FaBullhorn, FaFileAlt, FaFolderOpen, FaCheckCircle, FaClock, FaExclamationCircle, FaChartLine, FaExternalLinkAlt, FaCalendarAlt } from "react-icons/fa";

function PCHomeContainer() {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const [name, setName] = useState("");
    const [announcements, setAnnouncements] = useState([]);
    const [recentProjects, setRecentProjects] = useState([]);
    const [stats, setStats] = useState({
        totalProjects: 0,
        activeProjects: 0,
        pendingApprovals: 0,
    });
    const [loading, setLoading] = useState(true);

    const gotoReport = () => {
        navigate("/pc/report");
    };

    const gotoAnnouncement = () => {
        navigate("/pc/announcements");
    };

    const viewProject = (projectId) => {
        navigate(`/project/${projectId}`);
    };

    const gotoDefaulterList = () => {
        navigate("/pc/defaulter-list");
    };

    useEffect(() => {
        if (user) {
            setName(user?.name || "Project Coordinator");
        }

        const fetchData = async () => {
            if (!isAuthenticated) {
                // console.log("User not authenticated, skipping data fetch");
                setLoading(false);
                return;
            }

            try {
                await Promise.all([fetchAnnouncements(), fetchProjects()]);
            } catch (error) {
                console.error("Error fetching dashboard data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        AOS.init({ duration: 1500 });
    }, [user, isAuthenticated, navigate]);

    const fetchAnnouncements = async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/projects/announcements/general`
            );

            if (response.status !== 200) {
                throw new Error("Failed to fetch announcements");
            }

            const limitdata = response.data.slice(0, 2).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            setAnnouncements(limitdata);
            // console.log("Fetched announcements:", response.data);
        } catch (error) {
            console.error("Error fetching announcements:", error);
            console.error("Response data:", error.response?.data);
            console.error("Status:", error.response?.status);
        }
    };

    const fetchProjects = async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/projects/department/${user?.department}`
            );

            if (response.status !== 200) {
                throw new Error("Failed to fetch projects");
            }

            const data = response.data.data || [];

            const active = data.filter(
                (project) =>
                    project.status === "approved" ||
                    project.status === "guide-assigned"
            ).length;

            const pending = data.filter(
                (project) => project.status === "pending"
            ).length;

            setStats({
                totalProjects: data.length,
                activeProjects: active,
                pendingApprovals: pending,
            });

            const sortedProjects = data
                .sort(
                    (a, b) =>
                        new Date(b.createdAt || b.date) -
                        new Date(a.createdAt || a.date)
                )
                .slice(0, 5);

            setRecentProjects(sortedProjects);
            // console.log("Fetched projects:", data);
        } catch (error) {
            console.error("Error fetching projects:", error);
            console.error("Response data:", error.response?.data);
            console.error("Status:", error.response?.status);

            if (error.response?.status === 401) {
                navigate("/");
            }
        }
    };

    return (
        <div className="min-h-screen p-4 md:p-8 font-sans">
            {/* Header Section */}
            <div
                className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8"
                data-aos="fade-down"
            >
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
                        Project Coordinator Dashboard
                    </h1>
                    <p className="text-gray-500 mt-2 text-sm md:text-base">
                        Welcome back, <span className="font-semibold text-[#B7202E]">{name}</span>
                    </p>
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                        <FaCalendarAlt />
                        {new Date().toLocaleDateString("en-IN", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                        })}
                    </p>
                </div>
                <div className="flex flex-wrap gap-3">
                    <button
                        className="bg-[#B7202E] text-white px-5 py-2.5 rounded-xl font-bold shadow-sm hover:bg-[#961925] hover:shadow-md transition-all flex items-center gap-2 text-sm"
                        onClick={gotoAnnouncement}
                    >
                        <FaBullhorn /> Make Announcement
                    </button>
                    <button
                        className="bg-gray-800 text-white px-5 py-2.5 rounded-xl font-bold shadow-sm hover:bg-black hover:shadow-md transition-all flex items-center gap-2 text-sm"
                        onClick={gotoReport}
                    >
                        <FaFileAlt /> Generate Report
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-aos="fade-up">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 transition-transform hover:-translate-y-1 hover:shadow-md">
                    <div className="w-14 h-14 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-2xl">
                        <FaFolderOpen />
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-gray-800">{stats.totalProjects}</h3>
                        <p className="text-gray-500 text-sm font-medium">Total Projects</p>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 transition-transform hover:-translate-y-1 hover:shadow-md">
                    <div className="w-14 h-14 rounded-full bg-green-50 text-green-600 flex items-center justify-center text-2xl">
                        <FaCheckCircle />
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-gray-800">{stats.activeProjects}</h3>
                        <p className="text-gray-500 text-sm font-medium">Active Projects</p>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 transition-transform hover:-translate-y-1 hover:shadow-md">
                    <div className="w-14 h-14 rounded-full bg-yellow-50 text-yellow-600 flex items-center justify-center text-2xl">
                        <FaClock />
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-gray-800">{stats.pendingApprovals}</h3>
                        <p className="text-gray-500 text-sm font-medium">Pending Approvals</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Announcements Section */}
                <div className="lg:col-span-2 space-y-8">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-aos="fade-up">
                        <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2 border-b border-gray-100 pb-3">
                            <FaBullhorn className="text-[#B7202E]" /> Recent Announcements
                        </h2>

                        <div className="space-y-4">
                            {announcements.length > 0 ? (
                                announcements.map((announcement, index) => (
                                    <div key={index} className="bg-gray-50 border border-gray-100 rounded-xl p-5 hover:bg-white hover:shadow-sm transition-all">
                                        <div className="flex justify-between items-start mb-2">
                                            <h3 className="font-bold text-gray-800 text-lg">
                                                {announcement.title}
                                            </h3>
                                            <span className="text-xs text-gray-400 whitespace-nowrap bg-white px-2 py-1 rounded border border-gray-200">
                                                {new Date(announcement.date).toLocaleDateString("en-IN", {
                                                    day: 'numeric', month: 'numeric', year: 'numeric'
                                                })}
                                            </span>
                                        </div>
                                        <p className="text-gray-600 text-sm leading-relaxed">
                                            {announcement.message}
                                        </p>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                    <FaBullhorn className="mx-auto text-2xl mb-2 opacity-50" />
                                    <p>No announcements available</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Recent Activity Section */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 h-full" data-aos="fade-up" data-aos-delay="100">
                        <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2 border-b border-gray-100 pb-3">
                            <FaChartLine className="text-[#B7202E]" /> Recent Projects
                        </h2>

                        <div className="space-y-6 relative before:absolute before:left-[15px] before:top-2 before:h-[calc(100%-20px)] before:w-0.5 before:bg-gray-100">
                            {loading ? (
                                <div className="text-center py-8 text-gray-500">Loading projects...</div>
                            ) : recentProjects.length > 0 ? (
                                recentProjects.map((project, index) => (
                                    <div key={index} className="relative pl-10 group">
                                        <span className="absolute left-[11px] top-1.5 w-2.5 h-2.5 rounded-full bg-gray-300 border-2 border-white ring-1 ring-gray-100 group-hover:bg-[#B7202E] transition-colors"></span>
                                        <div>
                                            <p className="font-semibold text-gray-800 text-sm mb-1 leading-tight line-clamp-2" title={project.projectTitle || project.title}>
                                                {project.projectTitle || project.title}
                                            </p>

                                            <div className="flex flex-wrap gap-2 mb-2">
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide border ${(project.status === 'approved' || project.status === 'guide-assigned') ? 'bg-green-50 text-green-700 border-green-200' :
                                                    project.status === 'pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                                        'bg-gray-50 text-gray-600 border-gray-200'
                                                    }`}>
                                                    {project.status || "Pending"}
                                                </span>
                                                <span className="text-[10px] text-gray-400 flex items-center">
                                                    {new Date(project.createdAt || project.date).toLocaleDateString("en-IN", {
                                                        day: 'numeric', month: 'numeric', year: 'numeric'
                                                    })}
                                                </span>
                                            </div>

                                            {project.domain && project.domain.preference1 && (
                                                <p className="text-xs text-gray-500 mb-2">
                                                    <span className="font-medium">Domain:</span> {project.domain.preference1.name}
                                                </p>
                                            )}

                                            <button
                                                className="text-[#B7202E] text-xs font-semibold hover:underline flex items-center gap-1"
                                                onClick={() => viewProject(project._id || project.id)}
                                            >
                                                View Details <FaExternalLinkAlt className="text-[10px]" />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-8 text-gray-400 italic">
                                    No recent project activity
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PCHomeContainer;
